import { useContext, useState } from 'react';
import { Check, ChevronRight, Minus, Pause, Play, Plus } from 'lucide-react';
import { primeSpotifyPlayer } from '../../lib/spotifyPlayerPool';
import VoterAvatarStack from './VoterAvatarStack';
import Avatar from '../Avatar';
import { formatSetTimeRange, voteKey } from '../../utils/lineupFormat';
import { loadInBar, playInBar } from '../../lib/spotifyPlayerPool';
import { markDone } from '../../lib/moments';
import { PlayRowsContext, useNowPlaying } from './NowPlayingBar';
import CachedImg from '../ui/CachedImg';
import ArtistPlaceholder from './ArtistPlaceholder';

/**
 * Shared lineup row — voting modal, group lineup page, and read-only contexts.
 *
 * layout:
 *   vote  — FestivalMatchSetupModal (38/30 thumb, 28px vote toggle)
 *   view  — FestivalLineupViewModal (same row chrome, opens detail modal); with voters it also
 *           carries the group's overlap, as on the Sets page
 *   group — GroupLineupVotesList (28px thumb, avatar stack, 1/3 count)
 *   read  — minimal read-only row
 */
export default function ArtistRow({
  artist,
  size = 'support',
  layout = 'read',
  selected = false,
  onToggle,
  voters,
  onPress,
  totalVoters,
  showRoleBadge = false,
  emptyVotersLabel,
  dimmed = false,
  className = '',
  /** Sets page: a +/- on the row that picks the artist without opening them (operator, 2026-09-23). */
  onQuickToggle,
  picked = false,
  /** The first-time path's glow on the +, when this is the row it points at (Sets' first set). */
  nudge = '',
  /** Put the set's time before the stage in the subtext (the Sets list once times are out). */
  showTime = false,
}) {
  const isHeadliner = size === 'headliner';
  const votable = layout === 'vote' && typeof onToggle === 'function';
  const viewable = layout === 'view' && typeof onPress === 'function';
  const pressable = layout === 'group' && typeof onPress === 'function';
  const isGroup = layout === 'group';

  const thumbUrl = artist.spotify_image_url;
  // Play on the row (Sets, Sam 2026-10-23): plays in the now-playing bar without opening the artist. A b2b plays its first
  // artist and the bar offers the others. While this row plays: moving bars, a pause on hover, and a tap pauses.
  const playRows = useContext(PlayRowsContext);
  const nowPlaying = useNowPlaying();
  const players = (artist.members ?? [artist])
    .filter((m) => m.spotify_artist_id)
    .map((m) => ({
      spotifyId: m.spotify_artist_id,
      name: m.canonical_artist_name || m.artist_name,
    }));
  const rowKey = voteKey(artist.artist_id, artist.performance_day);
  const isCurrent = nowPlaying.key === rowKey;
  const isPlaying = isCurrent && !nowPlaying.paused;
  const [votersOpen, setVotersOpen] = useState(false);
  const stageLine = artist.stage || null;
  const timeRange = formatSetTimeRange([artist]);

  const rowClass = [
    'artist-row',
    isHeadliner ? 'artist-row--headliner' : 'artist-row--support',
    dimmed ? 'artist-row--dimmed' : '',
    onQuickToggle && picked ? 'artist-row--picked' : '',
    pressable || viewable ? 'cursor-pointer hover:opacity-90' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const thumbClass = isGroup
    ? 'artist-row__thumb artist-row__thumb--group'
    : `artist-row__thumb ${isHeadliner ? 'artist-row__thumb--headliner' : 'artist-row__thumb--support'}`;

  const nameClass = isGroup
    ? 'artist-row__name--group truncate block'
    : isHeadliner
      ? 'artist-row__name--headliner truncate block'
      : 'artist-row__name--support truncate block';

  const stageClass = isGroup
    ? 'artist-row__stage artist-row__stage--group truncate block'
    : 'artist-row__stage truncate block';

  const placeholder = <ArtistPlaceholder name={artist.artist_name} className={thumbClass} />;
  const thumbEl = thumbUrl ? (
    <CachedImg src={thumbUrl} alt="" className={thumbClass} loading="lazy" fallback={placeholder} />
  ) : (
    placeholder
  );

  const subtext = showTime
    ? [timeRange, stageLine].filter(Boolean).join(' · ')
    : layout === 'vote' || layout === 'view' || layout === 'group'
      ? stageLine
      : [showRoleBadge && isHeadliner ? 'Headliner' : null, timeRange, stageLine]
          .filter(Boolean)
          .join(' · ');

  const voteToggle = votable ? (
    <span
      className={`artist-row__vote-toggle ${selected ? 'artist-row__vote-toggle--selected' : ''}`}
      aria-hidden
    >
      {selected && <Check size={14} strokeWidth={2.4} className="text-ink" />}
    </span>
  ) : null;

  const votersCluster =
    voters && voters.length > 0 ? (
      <span className="artist-row__voters-cluster">
        <VoterAvatarStack people={voters} max={5} size={22} overlap={-7} />
        <span className="artist-row__vote-count">
          {totalVoters != null ? `${voters.length}/${totalVoters}` : `${voters.length}`}
        </span>
      </span>
    ) : emptyVotersLabel ? (
      <span className={dimmed ? 'artist-row__zero-votes' : 'text-micro text-[#5a5170]'}>
        {emptyVotersLabel}
      </span>
    ) : null;

  // The artist sheet's Spotify player starts switching as the finger lands, not when the sheet opens (spotifyPlayerPool).
  const prime = () =>
    primeSpotifyPlayer(
      (artist.members?.find((m) => m.spotify_artist_id) ?? artist).spotify_artist_id,
    );

  const mainContent = (
    <>
      {thumbEl}
      <span className="min-w-0 flex-1">
        <span className={nameClass}>{artist.artist_name}</span>
        {subtext && <span className={stageClass}>{subtext}</span>}
      </span>
      {voteToggle}
      {(isGroup || layout === 'view') && votersCluster}{' '}
      {/* a read-only view row (the map's stage preview) still says who in your group is going */}
      {isGroup && pressable && (
        <ChevronRight size={16} className="shrink-0 text-ink/40" aria-hidden />
      )}
      {viewable && <ChevronRight size={16} className="shrink-0 text-ink/40" aria-hidden />}
    </>
  );

  if (votable) {
    return (
      <button
        type="button"
        className={rowClass}
        onClick={() => onToggle(artist.artist_id, artist.performance_day)}
        aria-pressed={selected}
      >
        {mainContent}
      </button>
    );
  }

  const playBtn =
    playRows && viewable ? (
      players.length > 0 ? (
        <button
          type="button"
          className={`artist-row__play${isCurrent ? ' artist-row__play--current' : ''}${isPlaying ? ' artist-row__play--playing' : ''}`}
          onClick={() => playInBar(rowKey, players, isCurrent ? nowPlaying.index : 0)}
          aria-label={isPlaying ? `Pause ${artist.artist_name}` : `Play ${artist.artist_name}`}
        >
          {isPlaying ? (
            <>
              <span className="play-eq" aria-hidden>
                <i />
                <i />
                <i />
              </span>
              <Pause
                className="artist-row__play-pause"
                size={14}
                fill="currentColor"
                strokeWidth={0}
                aria-hidden
              />
            </>
          ) : (
            <Play size={14} fill="currentColor" strokeWidth={0} className="ml-[2px]" aria-hidden />
          )}
        </button>
      ) : (
        <span className="artist-row__play artist-row__play--none" aria-hidden />
      )
    ) : null;

  // Where rows play (Sets), the group's votes are a glass pill of their own (Sam's 1, 2026-10-23): two faces and the count,
  // purple when you are one of them. Only the pill opens the names, under the row (his B); the rest of the row still
  // loads the preview.
  const votesPill =
    playBtn && voters && voters.length > 0 ? (
      <button
        type="button"
        className={`artist-row__votes${picked ? ' artist-row__votes--mine' : ''}`}
        onClick={() => setVotersOpen((o) => !o)}
        aria-expanded={votersOpen}
        aria-label={`${voters.length} in your group picked ${artist.artist_name}`}
      >
        {/* Two whole faces, color ring only (Sam's A, 2026-11-04: tucked at 16 px the second was half hidden) */}
        <VoterAvatarStack
          people={voters}
          max={2}
          size={20}
          overlap={-3}
          showOverflow={false}
          className="voter-avatar-stack--ring-only"
        />
        {totalVoters != null ? `${voters.length}/${totalVoters}` : voters.length}
      </button>
    ) : null;
  const votersList =
    votesPill && votersOpen ? (
      <div className="artist-row__voters-list">
        {voters.map((v) => (
          <span key={v.id} className="artist-row__voter">
            <Avatar
              displayName={v.display_name}
              profilePhotoUrl={v.profile_photo_url}
              size="xxs"
              className="!h-[20px] !w-[20px]"
            />
            {v.display_name}
          </span>
        ))}
      </div>
    ) : null;

  // Where rows play, a tap on the row puts the artist in the bar without playing. A row Spotify lacks has nothing to open
  // there (Sam, 2026-10-26: no more old sheet for it), so only its + answers a tap.
  const openRow = playBtn
    ? players.length > 0
      ? () => loadInBar(rowKey, players)
      : null
    : () => onPress(artist);
  const OpenTag = openRow ? 'button' : 'div';

  // Quick pick: two siblings, never a button inside a button - the row opens the artist, the +/- picks.
  if (viewable && (typeof onQuickToggle === 'function' || playBtn)) {
    return (
      <div
        className={`${rowClass} artist-row--quick${votersList ? ' artist-row--voters-open' : ''}`}
      >
        <OpenTag
          {...(openRow
            ? { type: 'button', onPointerDown: playBtn ? undefined : prime, onClick: openRow }
            : {})}
          className="artist-row__open"
        >
          {thumbEl}
          <span className="min-w-0 flex-1">
            <span className={nameClass}>{artist.artist_name}</span>
            {subtext && <span className={stageClass}>{subtext}</span>}
          </span>
          {!votesPill && votersCluster}
        </OpenTag>
        {votesPill}
        {playBtn}
        {typeof onQuickToggle === 'function' && (
          <button
            type="button"
            className={`artist-row__quick${picked ? ' artist-row__quick--on' : ''}${nudge ? ` ${nudge}` : ''}`}
            onClick={() => {
              onQuickToggle(artist.artist_id, artist.performance_day);
              if (!picked) markDone('pick'); // the first-time path (lib/moments)
            }}
            aria-pressed={picked}
            aria-label={
              picked
                ? `Remove ${artist.artist_name} from your picks`
                : `Add ${artist.artist_name} to your picks`
            }
          >
            {picked ? (
              <Minus size={16} strokeWidth={2.4} aria-hidden />
            ) : (
              <Plus size={16} strokeWidth={2.4} aria-hidden />
            )}
          </button>
        )}
        {votersList}
      </div>
    );
  }

  if (viewable || pressable) {
    return (
      <button
        type="button"
        className={rowClass}
        onPointerDown={prime}
        onClick={() => onPress(artist)}
      >
        {mainContent}
      </button>
    );
  }

  return <div className={rowClass}>{mainContent}</div>;
}
